import React, { useState, useLayoutEffect, useMemo } from "react";
import cn from "classnames";
import style from "./common.module.scss";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "../../../hooks/useScreenSize";
import { getAmChartsLocale } from "../../../utils/chartDataProcessors";
import { useLanguage } from "../../../context/LanguageContext";
import { initUserName } from "../../../utils/shortenName";

const AppointmentMethodShare = ({ item }) => {
  const { t } = useTranslation();
  const { isMobileView } = useScreenSize();
  const { currentLanguage } = useLanguage();

  const methodColors = {
    assigned: "#4E7CFF",
    tender: "#22C0FF",
  };

const processData = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [
      {
        name: "Нет данных",
        shortName: "Нет данных",
        assigned: 0,
        tender: 0,
        assignedCount: 0,
        tenderCount: 0,
        total: 0,
      },
    ];
  }

  return data.map((employee) => {
    const total = employee.assigned + employee.tender;
    const assignedPercent = total > 0 ? (employee.assigned / total) * 100 : 0;
    const tenderPercent = total > 0 ? (employee.tender / total) * 100 : 0;

    return {
      name: employee.name,
      shortName: initUserName(employee.name), 
      assigned: assignedPercent,
      tender: tenderPercent,
      assignedCount: employee.assigned,
      tenderCount: employee.tender,
      total: total,
    };
  });
};

  const processedData = useMemo(() => {
    return processData(item);
  }, [item]);

  useLayoutEffect(() => {
    let root = am5.Root.new("chartdiv-client-employee-appointment-share");
    root.setThemes([am5themes_Animated.new(root)]);

    const locale = getAmChartsLocale(currentLanguage);
    if (locale) {
      root.locale = locale;
    }

    root._logo.dispose();

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        wheelX: "zoomY",
      }),
    );

    if (isMobileView) {
      chart.set("paddingLeft", 1);
    }
    chart.set("wheelable", true);

    let yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "shortName",
        renderer: am5xy.AxisRendererY.new(root, {
          minGridDistance: 30,
          inversed: true,
        }),
      }),
    );
    yAxis.data.setAll(processedData);

    let xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        max: 100,
        renderer: am5xy.AxisRendererX.new(root, {}),
      }),
    );

    const cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomY",
        yAxis: yAxis,
      }),
    );
    // cursor.lineX.set("visible", false);

    yAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    yAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
      visible: true,
    });

    xAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    xAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
      visible: true,
    });

    xAxis.setAll({
      min: 0,
      max: 100,
      strictMinMax: true,
      numberFormat: "#'%'",
    });

    let assignedSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: "Assigned",
        xAxis: xAxis,
        yAxis: yAxis,
        categoryYField: "shortName",
        valueXField: "assigned",
        stacked: true,
      }),
    );

    assignedSeries.columns.template.setAll({
      fill: am5.color(methodColors.assigned),
      stroke: am5.color(methodColors.assigned),
      maxHeight: 80,
    });

    assignedSeries.set(
      "tooltip",
      am5.Tooltip.new(root, {
        labelText: `${t("analytics.directPass")}: {assignedCount} ({valueX.formatNumber('#.0')}%)\n{name}`,
      }),
    );

    assignedSeries.bullets.push(function () {
      return am5.Bullet.new(root, {
        locationX: 0.5,
        locationY: 0.5,
        sprite: am5.Label.new(root, {
          text: "{valueX.formatNumber('#.0')}%",
          fill: am5.color("#FFFFFF"),
          fontSize: 11,
          centerX: am5.p50,
          centerY: am5.p50,
          populateText: true,
        }),
      });
    });

    assignedSeries.data.setAll(processedData);

    let tenderSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: "Tender",
        xAxis: xAxis,
        yAxis: yAxis,
        categoryYField: "shortName",
        valueXField: "tender",
        stacked: true,
      }),
    );

    tenderSeries.columns.template.setAll({
      fill: am5.color(methodColors.tender),
      stroke: am5.color(methodColors.tender),
      maxHeight: 80,
    });

    tenderSeries.set(
      "tooltip",
      am5.Tooltip.new(root, {
        labelText: `${t("analytics.tender")}: {tenderCount} ({valueX.formatNumber('#.0')}%)\n{name}`,
      }),
    );

    tenderSeries.bullets.push(function () {
      return am5.Bullet.new(root, {
        locationX: 0.5,
        locationY: 0.5,
        sprite: am5.Label.new(root, {
          text: "{valueX.formatNumber('#.0')}%",
          fill: am5.color("#FFFFFF"),
          fontSize: 11,
          centerX: am5.p50,
          centerY: am5.p50,
          populateText: true,
        }),
      });
    });

    tenderSeries.data.setAll(processedData);

    return () => {
      root.dispose();
    };
  }, [item, isMobileView, processedData, currentLanguage]);

  return (
    <div className={style.content}>
      <div className={style.filter}>
        <span className={style.filter_title}>{t("analytics.requestDistributionByEmployee")}, %</span>
      </div>

      <div className={cn(style.area__namesMobile)}>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <div className={style.area_icon} style={{ backgroundColor: methodColors.assigned }}></div>
          <div className={style.area_type}>{t("analytics.directPass")}</div>
        </div>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <div className={style.area_icon} style={{ backgroundColor: methodColors.tender }}></div>
          <div className={style.area_type}>{t("analytics.tender")}</div>
        </div>
      </div>

      <div className={style.area}>
        <div id="chartdiv-client-employee-appointment-share" className={style.area_chart}></div>
        <div className={style.area_names}>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div className={style.area_icon} style={{ backgroundColor: methodColors.assigned }}></div>
            <div className={style.area_type}>{t("analytics.directPass")}</div>
          </div>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div className={style.area_icon} style={{ backgroundColor: methodColors.tender }}></div>
            <div className={style.area_type}>{t("analytics.tender")}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentMethodShare;
